import { useState, useEffect } from "react";
import { Box, IconButton, CircularProgress, Typography } from "@mui/material";
import { MoreVert as MoreVertIcon, Edit as EditIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ActionsMenu from "../../components/ActionsMenu";
import DeleteConfirmationDialog from "../../components/DeleteDialog";
import DataTable from "../../components/DataTable";
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from "react-i18next";

const Inventory = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedInventoryId, setSelectedInventoryId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiUrl = "https://oven-backend.onrender.com";

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get(`${apiUrl}/inventory`);
        setInventory(response.data);
      } catch (error) {
        console.error("Error fetching inventory:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const handleMenuOpen = (event, inventoryId) => {
    setAnchorEl(event.currentTarget);
    setSelectedInventoryId(inventoryId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditInventory = () => {
    navigate(`/UpdateInventory/${selectedInventoryId}`);
    handleMenuClose();
  };

  const handleViewDetails = () => {
    navigate(`/ViewInventory/${selectedInventoryId}`);
    handleMenuClose();
  };

  const handleDeleteDialogOpen = () => {
    setOpenDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteDialogClose = () => {
    setOpenDeleteDialog(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`${apiUrl}/inventory/${selectedInventoryId}`);
      setInventory((prevInventory) => prevInventory.filter(item => item._id !== selectedInventoryId));
    } catch (error) {
      console.error("Error deleting inventory:", error);
    } finally {
      handleDeleteDialogClose();
    }
  };

  const columns = [
    { field: "itemName", headerName: t("item_name"), flex: 1 },
    { field: "totalQuantity", headerName: t("quantity"), flex: 1 },
    { field: "unitPrice", headerName: t("price"), flex: 1, renderCell: (params) => (<Typography color="green">$  {params.row.unitPrice} </Typography> ) },
    { field: "minStockLevel", headerName: t("min_stock_level"), flex: 1 },
    { field: "lastPurchaseDate", headerName: t("date"), flex: 1, valueGetter: ({ value }) => value ? new Date(value).toLocaleDateString() : "N/A" },
    {
      field: "actions",
      headerName: t("actions"),
      flex: 0.5,
      renderCell: (params) => (
        <Box>
          <IconButton onClick={(event) => handleMenuOpen(event, params.row._id)}>
            <MoreVertIcon />
          </IconButton>
          <ActionsMenu 
            anchorEl={anchorEl}
            handleMenuClose={handleMenuClose}
            menuItems={[
              { label: t("edit_inventory"), action: handleEditInventory, icon: <EditIcon /> },
              { label: t("view_details"), action: handleViewDetails, icon: <VisibilityIcon />},
              { label: t("delete_inventory"), action: handleDeleteDialogOpen, icon: <DeleteIcon />, style: { color: "red" } }
            ]}
          />
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px" sx={{ direction: isRTL ? "rtl" : "ltr" }}>
      {loading ? (
        <CircularProgress />
      ) : (
        <DataTable 
          title={t("inventory_list")}
          columns={columns}
          rows={inventory} 
          getRowId={(row) => row._id}
        />
      )}

      <DeleteConfirmationDialog
        open={openDeleteDialog} 
        onClose={handleDeleteDialogClose} 
        onConfirm={handleConfirmDelete} 
        title={t("delete_inventory_item")}
        message={t("delete_inventory_confirmation")}
        confirmColor="error"
        cancelColor="secondary"
        size="large"
      />
    </Box>
  );
};

export default Inventory;
